import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Paper, TextField, Typography } from '@mui/material';
import { publicApi } from '../services/api';
import { usePrivatePage } from './common';

/** A one-time "set your password" link (7k), for new partner accounts and forgotten passwords. */
const SetPasswordPage = () => {
  const { uid, token } = useParams();
  usePrivatePage('Set your password · Ranger');
  const [account, setAccount] = useState({ loading: true });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const url = `/auth/set-password/${encodeURIComponent(uid)}/${encodeURIComponent(token)}/`;

  useEffect(() => {
    publicApi.get(url)
      .then(({ data }) => setAccount({ username: data.username }))
      .catch((err) => setAccount({ error: err.response?.data?.error || "This link can't be checked just now. Try again later." }));
  }, [url]);

  const submit = async (e) => {
    e.preventDefault();
    setPending(true);
    setError(null);
    try {
      await publicApi.post(url, { password });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.error || "The password couldn't be set just now. Try again later.");
    } finally {
      setPending(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f6f8', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Paper variant="outlined" sx={{ p: 4, borderRadius: 3, width: '100%', maxWidth: 420 }}>
        <Typography variant="h5" fontFamily="'Work Sans', sans-serif" fontWeight={700} sx={{ mb: 2 }}>Set your password</Typography>
        {account.loading && <CircularProgress size={24} />}
        {account.error && <Typography>{account.error}</Typography>}
        {account.username && done && (
          <>
            <Alert severity="success" sx={{ mb: 2 }}>Your password is set.</Alert>
            <Button component={Link} to="/login" variant="contained" sx={{ textTransform: 'none' }}>Sign in as {account.username}</Button>
          </>
        )}
        {account.username && !done && (
          <Box component="form" onSubmit={submit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              For the account <strong>{account.username}</strong>. At least 8 characters; this link works once.
            </Typography>
            <TextField label="New password" type="password" autoComplete="new-password" required value={password}
              onChange={(e) => setPassword(e.target.value)} autoFocus />
            <TextField label="The same password again" type="password" autoComplete="new-password" required value={confirm}
              onChange={(e) => setConfirm(e.target.value)} error={confirm !== '' && confirm !== password}
              helperText={confirm !== '' && confirm !== password ? "These don't match." : ' '} />
            {error && <Alert severity="error">{error}</Alert>}
            <Button type="submit" variant="contained" disabled={pending || !password || password !== confirm} sx={{ textTransform: 'none' }}>
              {pending ? 'Saving…' : 'Set password'}
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default SetPasswordPage;
